import { C as Cart } from './cart-BEbKDMPu.js';
import { P as PubSub } from './pubsub-CoPYzvVm.js';
import { N as Notyf } from './notyf-DIw0RvBj.js';
import './uniq-BDEfcc0N.js';

/**
 * @description Observes `<product-form>` for Recharge widgets and hides `[data-variant-price]` labels.
 * Legacy `.rc-widget` uses plain HTML and hides prices as soon as it is found.
 * `recharge-subscription-widget` renders inside a shadow root; prices hide only when
 * `.rc-widget__root` is present inside that shadow tree.
 */
class ProductFormRecharge {
  /** @type {HTMLElement} */
  #host;
  /** @type {MutationObserver | null} */
  #mountObserver = null;
  /** @type {MutationObserver | null} */
  #shadowObserver = null;
  /** @type {number | null} */
  #shadowPollFrame = null;
  /** @type {Element | null} */
  #watchedShadowWidget = null;

  /**
   * @param {HTMLElement} host - The `<product-form>` element
   */
  constructor(host) {
    this.#host = host;
  }

  connect() {
    this.#watchMount();
    this.#tryActivateWidget();
  }

  disconnect() {
    this.#stopMountObserver();
    this.#stopShadowObserver();
  }

  #tryActivateWidget() {
    const legacyWidget = this.#host.querySelector('.rc-widget');
    if (legacyWidget) {
      this.#stopMountObserver();
      this.#stopShadowObserver();
      this.#hideVariantPrices();
      return;
    }

    const v2Widget = this.#host.querySelector('recharge-subscription-widget');
    if (!v2Widget) return;

    if (this.#hasV2WidgetContent(v2Widget)) {
      this.#stopMountObserver();
      this.#stopShadowObserver();
      this.#hideVariantPrices();
      return;
    }

    this.#watchShadowContent(v2Widget);
  }

  /**
   * @param {Element} widget
   * @returns {boolean}
   */
  #hasV2WidgetContent(widget) {
    return Boolean(widget.shadowRoot?.querySelector('.rc-widget__root'));
  }

  #watchMount() {
    if (this.#mountObserver) return;

    this.#mountObserver = new MutationObserver(() => this.#tryActivateWidget());
    this.#mountObserver.observe(this.#host, { childList: true, subtree: true });
  }

  #stopMountObserver() {
    this.#mountObserver?.disconnect();
    this.#mountObserver = null;
  }

  /**
   * @param {Element} widget
   */
  #watchShadowContent(widget) {
    if (this.#watchedShadowWidget === widget && (this.#shadowObserver || this.#shadowPollFrame)) {
      return;
    }

    this.#stopShadowObserver();
    this.#watchedShadowWidget = widget;

    const check = () => {
      this.#shadowPollFrame = null;

      if (!this.#host.contains(widget)) {
        this.#stopShadowObserver();
        return;
      }

      if (this.#hasV2WidgetContent(widget)) {
        this.#stopMountObserver();
        this.#stopShadowObserver();
        this.#hideVariantPrices();
        return;
      }

      const shadow = widget.shadowRoot;
      if (shadow) {
        if (!this.#shadowObserver) {
          this.#shadowObserver = new MutationObserver(check);
          this.#shadowObserver.observe(shadow, { childList: true, subtree: true });
        }
        return;
      }

      this.#shadowPollFrame = requestAnimationFrame(check);
    };

    check();
  }

  #stopShadowObserver() {
    this.#shadowObserver?.disconnect();
    this.#shadowObserver = null;

    if (this.#shadowPollFrame) {
      cancelAnimationFrame(this.#shadowPollFrame);
      this.#shadowPollFrame = null;
    }

    this.#watchedShadowWidget = null;
  }

  #hideVariantPrices() {
    document.querySelectorAll('[data-variant-price]').forEach((label) => {
      label.classList.add('hidden');
      label.classList.remove('invisible');
    });
  }
}

/** Teleported modals (`data-teleport` in dialog.js) are moved to `document.body`, so replacing `<product-form>` does not remove them — clear before swap to avoid duplicate formula-calculator modals on variant change. */
const FORMULA_CALCULATOR_MODAL_SELECTOR = 'modal-element[id^="formula-calculator-"]';

class ProductForm extends HTMLElement {
  /** @type {ProductFormRecharge | null} */
  #recharge = null;
  #unsubscribers = [];
  #form = this.querySelector('form');
  #quantityInput = this.#form?.querySelector('input[name="quantity"]');
  #maxOrderAmount = this.querySelector('quantity-input')?.getAttribute('data-max-order-amount');
  // Get all submit buttons that are part of the form
  #submitButtons = this.#form
    ? document.querySelectorAll(`button[form="${this.#form.getAttribute('id')}"]`)
    : [];

  connectedCallback() {
    // No form yet (e.g. ATC block missing) — avoid crashing field init / listeners
    if (!this.#form) return;

    this.#submitButtons.forEach((button) => {
      if (button.disabled) button.dataset.originallyDisabled = 'true';
    });

    this.#form.addEventListener('submit', (event) => this.#onFormSubmit(event));

    this.#recharge = new ProductFormRecharge(this);
    this.#recharge.connect();

    this.#unsubscribers.push(
      PubSub.subscribe('variant:change', (event) => this.#onVariantChange(event)),
      PubSub.subscribe('quantity:change', ({ quantity }) => {
        if (this.#quantityInput) this.#quantityInput.value = quantity;
      }),
      PubSub.subscribe('variant:changing', ({ id }) => {
        if (id !== this.dataset.productId) return;
        this.#toggleLoading(true);
      })
    );
  }

  disconnectedCallback() {
    this.#recharge?.disconnect();
    this.#recharge = null;
    this.#unsubscribers.forEach((unsubscribe) => unsubscribe());
  }

  /**
   * Replace this form when the matching product variant changes.
   * @param {{ id: string, data: { html: Document | Element, variantId?: string } }} event
   */
  #onVariantChange({ id, data: { html, variantId } }) {
    if (id !== this.dataset.productId) return;

    this.#toggleLoading(false);
    document.querySelectorAll(FORMULA_CALCULATOR_MODAL_SELECTOR).forEach((node) => node.remove());

    const productForm = html.querySelector('product-form')?.cloneNode(true);
    if (!productForm) return;

    this.querySelector('recharge-legacy-widget')?.preserveAppBlockAcrossSwap?.(productForm);
    this.replaceWith(productForm);
    this.#syncRechargeVariantAfterSwap(productForm, variantId);
  }

  /**
   * Sync Recharge after swap so the preserved mount and new form inputs are in place.
   * @param {Element} productForm
   * @param {string | undefined} variantId
   */
  #syncRechargeVariantAfterSwap(productForm, variantId) {
    const widget = productForm.querySelector('recharge-legacy-widget');
    if (!widget?.syncVariant) return;

    const nextVariantId =
      variantId ?? productForm.querySelector('input[name="variant-id"]')?.value;
    widget.syncVariant(nextVariantId);
  }

  /**
   * Handle form submission
   * @param {Event} event
   */
  async #onFormSubmit(event) {
    event.preventDefault();
    this.#toggleLoading(true);

    try {
      await Cart.add({ form: this.#form });
      await this.#syncMaxOrderFromCart();
      PubSub.publish('dialog:open', { id: 'cart-drawer' });
    } catch (error) {
      this.#onError(error);
    } finally {
      this.#toggleLoading(false);
    }
  }

  /**
   * Toggle loading state on submit buttons
   * @param {boolean} loading
   */
  #toggleLoading(loading) {
    this.#submitButtons.forEach((button) => button.classList.toggle('loading', loading));
  }

  /**
   * @param {boolean} disabled
   */
  #toggleSubmitDisabledState(maxReached) {
    this.#submitButtons.forEach((button) => {
      if (maxReached) {
        button.dataset.maxOrderDisabled = '';
        button.disabled = true;
      } else if (button.dataset.maxOrderDisabled !== undefined) {
        delete button.dataset.maxOrderDisabled;
        if (button.dataset.originallyDisabled !== 'true') {
          button.disabled = false;
        }
      }
    });
    this.#toggleMaxOrderTextVisibility(maxReached);
  }

  /**
   * @param {boolean} visible
   */
  #toggleMaxOrderTextVisibility(visible) {
    const maxOrderText = this.querySelector('[data-max-order-text]');
    if (!maxOrderText) {
      return;
    }

    maxOrderText.classList.toggle('hidden', !visible);
  }

  /**
   * @description Sync max-order state from the current cart (product total across variants).
   * @returns {Promise<void>}
   */
  async #syncMaxOrderFromCart() {
    const maxOrderAmount = Number(this.#maxOrderAmount);
    if (!Number.isFinite(maxOrderAmount) || maxOrderAmount <= 0) {
      return;
    }

    const productId = Number(this.dataset.productId);

    try {
      const cart = await Cart.get();
      const productCartQuantity = this.#getProductCartQuantity(cart.items, productId);
      const maxReached = productCartQuantity >= maxOrderAmount;
      this.#updateMaxOrderState(maxReached, productCartQuantity);
    } catch {
      // Cart fetch failed; keep current UI state
    }
  }

  /**
   * @param {Array<{ product_id?: number, quantity?: number }>} items
   * @param {number} productId
   * @returns {number}
   */
  #getProductCartQuantity(items, productId) {
    return (items ?? []).reduce((total, item) => {
      if (Number(item.product_id) === productId) {
        return total + Number(item.quantity);
      }
      return total;
    }, 0);
  }

  /**
   * @param {boolean} maxReached
   * @param {number} productCartQuantity
   */
  #updateMaxOrderState(maxReached, productCartQuantity) {
    this.#toggleSubmitDisabledState(maxReached);
    this.querySelector('quantity-input')?.setMaxOrderState({ maxReached, productCartQuantity });
  }

  /**
   * Handle form error
   * @param {Error} error - The error object
   */
  #onError({ message }) {
    Notyf.error(message);
  }
}

customElements.define('product-form', ProductForm);
